import { pluralize } from '../../i18n/pluralize';
import { formatDayMonth, formatFullDate } from '../../i18n/formatDate';
import { withLangPrefix } from '../../i18n/locale';
import translations from './wiki.translations';
import { wikiProse } from './wikiTypography';
import { WikiCategory, WikiExterior, WikiLang, WikiRareKind, WikiVariantKind, type WikiRarity } from './wiki.types';

export type WikiTextKey = keyof typeof translations.en;

export type WikiT = (key: WikiTextKey, params?: Record<string, string | number>) => string;

export const wikiText = (lang: WikiLang): WikiT => {
  const dictionary = translations[lang];
  return (key, params) =>
    params ? dictionary[key].replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(params[name] ?? '')) : dictionary[key];
};

// WHY: экраны выводят подписи через типограф; метаданные и хлебные крошки JSON-LD берут сырой wikiText
export const wikiUiText = (lang: WikiLang): WikiT => {
  const t = wikiText(lang);
  return (key, params) => wikiProse(t(key, params));
};

export const wikiHref = (lang: WikiLang, path: string): string => withLangPrefix(lang, path);

export const EXTERIORS: WikiExterior[] = [WikiExterior.Fn, WikiExterior.Mw, WikiExterior.Ft, WikiExterior.Ww, WikiExterior.Bs];

export const WIKI_MIN_QUERY_LENGTH = 2;

export const WIKI_KINDS: WikiVariantKind[] = [WikiVariantKind.Normal, WikiVariantKind.Stattrak, WikiVariantKind.Souvenir];

export const WIKI_LANGS: WikiLang[] = [WikiLang.En, WikiLang.Ru, WikiLang.Uz, WikiLang.Tr, WikiLang.Pt];

export const toWikiLang = (value: string | null | undefined): WikiLang | null => WIKI_LANGS.find((l) => l === value) ?? null;

const EXTERIOR_KEY: Record<WikiExterior, WikiTextKey> = {
  fn: 'exteriorFn',
  mw: 'exteriorMw',
  ft: 'exteriorFt',
  ww: 'exteriorWw',
  bs: 'exteriorBs',
};

export const exteriorName = (t: WikiT, exterior: WikiExterior): string => t(EXTERIOR_KEY[exterior]);

const KIND_KEY: Record<WikiVariantKind, WikiTextKey> = {
  normal: 'kindNormal',
  stattrak: 'kindStatTrak',
  souvenir: 'kindSouvenir',
};

export const kindName = (t: WikiT, kind: WikiVariantKind): string => t(KIND_KEY[kind]);

const CATEGORY_KEY: Record<WikiCategory, WikiTextKey> = {
  pistols: 'catPistols',
  rifles: 'catRifles',
  smgs: 'catSmgs',
  heavy: 'catHeavy',
  knives: 'catKnives',
  gloves: 'catGloves',
  equipment: 'catEquipment',
};

export const categoryName = (t: WikiT, category: WikiCategory): string => t(CATEGORY_KEY[category]);

const RARE_KIND_KEY: Record<WikiRareKind, { name: WikiTextKey; hint: WikiTextKey }> = {
  knives: { name: 'rareKnives', hint: 'rareHintKnives' },
  gloves: { name: 'rareGloves', hint: 'rareHintGloves' },
  knives_and_gloves: { name: 'rareKnivesAndGloves', hint: 'rareHintKnivesAndGloves' },
};

export const rareKindName = (t: WikiT, kind: WikiRareKind): string => t(RARE_KIND_KEY[kind].name);

// WHY: API отдаёт строку игры как есть, у перчаток в ru она со строчной («экстраординарного типа»); правим только первую букву
export const rarityLabel = (rarity: WikiRarity, lang: WikiLang): string =>
  rarity.name.charAt(0).toLocaleUpperCase(lang) + rarity.name.slice(1);

export const rareKindHint = (t: WikiT, kind: WikiRareKind): string => t(RARE_KIND_KEY[kind].hint);

export const WIKI_CATEGORIES: WikiCategory[] = [
  WikiCategory.Pistols,
  WikiCategory.Rifles,
  WikiCategory.Smgs,
  WikiCategory.Heavy,
  WikiCategory.Knives,
  WikiCategory.Gloves,
  WikiCategory.Equipment,
];

export const skinCount = (t: WikiT, lang: WikiLang, count: number): string =>
  pluralize(count, { one: t('skinsOne', { count }), few: t('skinsFew', { count }), many: t('skinsMany', { count }) }, lang);

const RARE_COUNT_KEY: Record<WikiRareKind, [WikiTextKey, WikiTextKey, WikiTextKey]> = {
  knives: ['knivesOne', 'knivesFew', 'knivesMany'],
  gloves: ['glovesOne', 'glovesFew', 'glovesMany'],
  knives_and_gloves: ['rareItemsOne', 'rareItemsFew', 'rareItemsMany'],
};

export const rareCount = (t: WikiT, lang: WikiLang, kind: WikiRareKind, count: number): string => {
  const [one, few, many] = RARE_COUNT_KEY[kind];
  return pluralize(count, { one: t(one, { count }), few: t(few, { count }), many: t(many, { count }) }, lang);
};

export const formatWikiDate = (date: string, lang: WikiLang): string => formatDayMonth(date, lang, { utc: true, year: true });

const SENTENCE_BREAK = /(?<=[.!?…][»"”)]*)\s+(?=[\p{Lu}«"“(\d])/u;

// WHY: анонс на плитке — целые предложения с начала, а не обрыв на полуслове
export const wikiExcerpt = (text: string, max: number): string => {
  const [first, ...rest] = text.split(SENTENCE_BREAK);
  let excerpt = first;
  for (const sentence of rest) {
    if (excerpt.length + 1 + sentence.length > max) break;
    excerpt = `${excerpt} ${sentence}`;
  }
  return excerpt;
};

// WHY: дата выхода — день появления в файлах игры, число и месяц неточны; показываем только год
export const wikiYear = (date: string): string => date.slice(0, 4);

export const formatWikiFullDate =(date: string, lang: WikiLang): string => formatFullDate(date, lang, { utc: true });

// WHY: uz-UZ в Chromium печатает «$ 1,695.00», а в Node и WebKit — «1 695,00 US$»; узбекские числа собираем по ru-RU, чтобы SSR и браузер совпадали
const NUMBER_LOCALE: Record<WikiLang, string> = { en: 'en-US', ru: 'ru-RU', uz: 'ru-RU', tr: 'tr-TR', pt: 'pt-BR' };

export const formatWikiNumber = (value: number, lang: WikiLang, digits: number): string =>
  new Intl.NumberFormat(NUMBER_LOCALE[lang], { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);

export const formatWikiPrice = (usd: number, lang: WikiLang): string =>
  lang === 'uz'
    ? `${formatWikiNumber(usd, lang, 2)} US$`
    : new Intl.NumberFormat(NUMBER_LOCALE[lang], { style: 'currency', currency: 'USD' }).format(usd);

const floatDigits = (value: number): number => (Number.isInteger(Math.round(value * 1000) / 10) ? 2 : 3);

export const formatWikiFloatRange = (min: number, max: number, lang: WikiLang): string =>
  `${formatWikiNumber(min, lang, floatDigits(min))}–${formatWikiNumber(max, lang, floatDigits(max))}`;

export const splitSkinName = (name: string): { weapon: string; finish: string } => {
  const at = name.indexOf(' | ');
  return at === -1 ? { weapon: name, finish: '' } : { weapon: name.slice(0, at), finish: name.slice(at + 3) };
};
